import React from 'react'
import styled from 'styled-components'

import {
    HomepageV3Query,
    HomepageV3RepeatableQuery,
    HeroFragment,
    FeatureBlockFragment,
    GetStartedFragment,
    StatisticsFragment,
    IntegrationsFragment,
    CustomerLogosPlusCtaFragment,
    VideoTabsFragment,
    VideoWithOutlineFragment,
    RotatingBannerFragment,
    HomepageV3RepeatableHeroFragment,
    HomepageV3RepeatableFeatureBlockFragment,
    HomepageV3RepeatableStatisticsFragment,
    HomepageV3RepeatableIntegrationsFragment,
    HomepageV3RepeatableCustomerLogosPlusCtaFragment,
    HomepageV3RepeatableVideoTabsFragment,
    HomepageV3RepeatableVideoWithOutlineFragment,
    HomepageV3RepeatableRotatingBannerFragment,
    HomepageV3RepeatableGetStartedFragment,
} from '@/graphql-types'
import {FeatureBlockSlice} from '@/slices/feature-block'
import {GetStartedSlice} from '@/slices/get-started'
import {StatisticsSlice} from '@/slices/statistics'
import mq from '@helios/shared/utils/media-query'

import {CustomerLogosWithCta} from './customer-logos-cta'
import {Hero} from './hero'
import {Integrations} from './integrations'
import {RotatingBanner} from './rotating-banner'
import {VideoTabs} from './video-tabs'
import {VideoWithAnimatedOutline} from './video-with-animated-outline'

type SliceData =
    | HomepageV3Query['prismicHomepageV3']['data']['body'][number]
    | HomepageV3RepeatableQuery['prismicHomepageV3Repeatable']['data']['body'][number]

enum SliceTypename {
    Hero = 'hero',
    FeatureBlock = 'feature_block',
    CustomerLogosPlusCta = 'customer_logos_plus_cta',
    GetStarted = 'get_started',
    VideoTabs = 'video_tabs',
    VideoWithOutline = 'video_with_outline',
    Statistics = 'stats',
    Integrations = 'integrations',
    RotatingBanner = 'rotating_banner',
}

type Props = {
    slices: SliceData[]
}

export const SliceZone: React.FC<Props> = ({slices}) => (
    <>
        {slices.map((slice, i) => {
            if (
                (!slice.slice_type && slice.hasOwnProperty('primary')) ||
                (!slice.slice_type && slice.hasOwnProperty('fields'))
            ) {
                throw new Error(`Missing type field in slice: ${JSON.stringify(slice)}`)
            }

            const key = `${slice.slice_type}-${i}`

            switch (slice.slice_type) {
                case SliceTypename.Hero:
                    return (
                        <Hero
                            key={key}
                            slice={slice as HeroFragment | HomepageV3RepeatableHeroFragment}
                        />
                    )

                case SliceTypename.FeatureBlock:
                    return (
                        <FeatureBlockSlice
                            key={key}
                            slice={
                                slice as
                                    | FeatureBlockFragment
                                    | HomepageV3RepeatableFeatureBlockFragment
                            }
                        />
                    )

                case SliceTypename.GetStarted:
                    return (
                        <GetStartedSlice
                            key={key}
                            slice={
                                slice as HomepageV3RepeatableGetStartedFragment | GetStartedFragment
                            }
                        />
                    )

                case SliceTypename.Statistics:
                    return (
                        <StatisticsSlice
                            key={key}
                            slice={
                                slice as StatisticsFragment | HomepageV3RepeatableStatisticsFragment
                            }
                        />
                    )

                case SliceTypename.Integrations:
                    const integrationsContent = slice as
                        | IntegrationsFragment
                        | HomepageV3RepeatableIntegrationsFragment
                    return (
                        <Integrations
                            key={key}
                            logo1={integrationsContent.primary.logo_1}
                            logo2={integrationsContent.primary.logo_2}
                            logo3={integrationsContent.primary.logo_3}
                            moreText={integrationsContent.primary.more_text}
                            ctaText={integrationsContent.primary.cta_text}
                            ctaLink={integrationsContent.primary.cta_link}
                            title={integrationsContent.primary.title}
                        />
                    )

                case SliceTypename.CustomerLogosPlusCta:
                    const customerLogosContent = slice as
                        | CustomerLogosPlusCtaFragment
                        | HomepageV3RepeatableCustomerLogosPlusCtaFragment

                    return (
                        <CustomerLogosWithCta
                            key={key}
                            ctaText={customerLogosContent.primary.cta_text}
                            ctaLink={customerLogosContent.primary.cta_link}
                            title={customerLogosContent.primary.title}
                            logos={customerLogosContent.items.map(({logo}) => logo)}
                        />
                    )

                case SliceTypename.RotatingBanner:
                    const rotatingBannerContent = slice as
                        | RotatingBannerFragment
                        | HomepageV3RepeatableRotatingBannerFragment

                    return (
                        <HiddenBelowMd key={key}>
                            <RotatingBanner
                                items={rotatingBannerContent.items.map(({text}) => text)}
                            />
                        </HiddenBelowMd>
                    )

                case SliceTypename.VideoTabs:
                    const videoTabsContent = slice as
                        | VideoTabsFragment
                        | HomepageV3RepeatableVideoTabsFragment

                    const tabs = videoTabsContent.items.map((item) => {
                        return {
                            ...item,
                            video: {
                                mp4: item.video_mp4?.url,
                                webm: item.video_webm?.url,
                            },
                        }
                    })

                    return (
                        <HiddenBelowMd key={key}>
                            <VideoTabs tabs={tabs} />
                        </HiddenBelowMd>
                    )

                case SliceTypename.VideoWithOutline:
                    const videoWithOutlineContent = slice as
                        | VideoWithOutlineFragment
                        | HomepageV3RepeatableVideoWithOutlineFragment

                    const content = {
                        section_title: videoWithOutlineContent.primary.title,
                        video_list: videoWithOutlineContent.items,
                    }

                    return <VideoWithAnimatedOutline key={key} data={content} />

                default:
                    if (slice.slice_type) {
                        throw new Error(`Unknown slice: ${JSON.stringify(slice)}`)
                    } else {
                        return null
                    }
            }
        })}
    </>
)

const HiddenBelowMd = styled.div`
    ${mq.max('md')} {
        display: none;
    }
`
